import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useEffect, useState } from 'react';
import { FlatList, Image, Modal, ScrollView, TouchableOpacity, View } from 'react-native';
import { faFilter, faFileMp4, faFilePdf, faFileJpg, faFile } from '@fortawesome/pro-solid-svg-icons';
import { createStyleSheet, useStyles } from 'react-native-unistyles';
import { WindowInformation } from '../../../services/window-information';
import MyAppText from '../../../components/MyAppText';
import { translate, translateWithoutErrors } from '../../../services/translate';
import moment from 'moment';
import CustomButton from '../../../components/CustomButton';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { faX } from '@fortawesome/free-solid-svg-icons';
import { MyCheckbox } from '../../../components/MyCheckbox';
import Toast from 'react-native-toast-message';
import { appAgentService } from '../../../services/central-api/app-agent';
import ImageModal from './ImageModal';
import VideoModal from './VideoModal';

interface Props {
    occurrenceId: number;
    setFilters: React.Dispatch<React.SetStateAction<{
        attachment: boolean | undefined;
        report: boolean | undefined;
        actions: boolean | undefined;
    }>>;
    filters: {
        attachment: boolean | undefined;
        report: boolean | undefined;
        actions: boolean | undefined;
    };
}

export default function AgentOccurrenceHistoric({ occurrenceId, filters, setFilters }: Props) {
    const windowInfo = WindowInformation();
    const { styles, theme } = useStyles(styleSheet);

    const [attachment, setAttachment] = useState<boolean>(filters.attachment ?? false);
    const [report, setReport] = useState<boolean>(filters.attachment ?? false);
    const [actions, setActions] = useState<boolean>(filters.actions ?? false);

    const [historic, setHistoric] = useState<OccurrenceReport[]>([]);
    const [isModalVisible, setIsModalVisible] = useState<boolean>(false);

    const [isImageModalVisible, setIsImageModalVisible] = useState(false);
    const [isVideoModalVisible, setIsVideoModalVisible] = useState(false);

    const [url, setUrl] = useState<string | undefined>();

    function renderAttachment(data: { url: string; type: string; additionalInfo?: { reportPolice?: string; }; }) {
        const getIcon = () => {
            if (data.type.includes('image/')) {
                return faFileJpg as IconDefinition;
            }
            if (data.type.includes('video/')) {
                return faFileMp4 as IconDefinition;
            }
            if (data.type.includes('application/pdf')) {
                return faFilePdf as IconDefinition;
            }
            return faFile as IconDefinition;
        };

        const renderDownloadButton = () => (
            <CustomButton
                icon={getIcon()}
                text={translate('download')}
                onPress={() => window.open(data.url)}
            />
        );

        if (data.type.includes('image/')) {
            return (
                <View style={{ flexDirection: 'column', rowGap: 8, alignItems: 'center' }}>
                    <TouchableOpacity
                        onPress={() => {
                            setUrl(data.url);
                            setIsImageModalVisible(true);
                        }}
                    >
                        <Image source={{ uri: data.url }} style={styles.imagePreview} />
                    </TouchableOpacity>
                    {renderDownloadButton()}
                </View>
            );
        }

        if (data.type.includes('video/')) {
            return (
                <View style={{ flexDirection: 'row', gap: 8, justifyContent: 'center' }}>
                    <CustomButton
                        onPress={() => {
                            setIsVideoModalVisible(true);
                            setUrl(data.url);
                        }}
                        text={translate('watch')}
                    />
                    {renderDownloadButton()}
                </View>
            );
        }

        return (
            <View style={{ flexDirection: 'row', justifyContent: 'center' }}>
                {renderDownloadButton()}
            </View>
        );
    }

    function renderHistoric({ item }: { item: OccurrenceReport; }) {
        const content = ['report', 'final_report', 'handcuff_explanation'].includes(item.occurrenceReportType) ? item.content : JSON.parse(item.content);
        return (
            <View style={styles.historicCard}>

                {item.occurrenceReportType == 'attachment' ?
                    <View style={{ gap: 10, flexDirection: 'column' }}>
                        <View style={{ flexDirection: 'column', gap: 16 }}>
                            <View>
                                <MyAppText style={styles.cardHeader}>{translate('addedAttachment')}</MyAppText>
                                <MyAppText style={styles.cardSubtitle}>
                                    {item.user ? (item.user.warName || item.user.name) + ' • ' : ''}
                                    {moment(item.createdAt).format('DD/MM/YYYY HH:mm:ss')}
                                </MyAppText>
                            </View>
                            {Object.keys(content).includes('additionalInfo') &&
                                <MyAppText style={styles.cardContent}>
                                    {
                                        Object.keys(content.additionalInfo).map((key) => {
                                            return `${translateWithoutErrors(key)}: ${String(content.additionalInfo[key])}`;
                                        }
                                        ).join('\n')
                                    }
                                </MyAppText>
                            }
                        </View>
                        <View style={{ flex: 1 }}>
                            {renderAttachment(content)}
                        </View>
                    </View>
                    : null
                }

                {['report', 'final_report', 'handcuff_explanation'].includes(item.occurrenceReportType) ?
                    <View style={{ gap: 10 }}>
                        <View>
                            <MyAppText style={styles.cardHeader}>
                                {item.occurrenceReportType === 'report' ? translate('addedComment') : null}
                                {item.occurrenceReportType === 'final_report' ? translate('final_report') : null}
                                {item.occurrenceReportType === 'handcuff_explanation' ? translate('handcuff_explanation') : null}
                            </MyAppText>
                            <MyAppText style={styles.cardSubtitle}>
                                {item.user ? (item.user.warName || item.user.name) + ' • ' : ''}
                                {moment(item.createdAt).format('DD/MM/YYYY HH:mm:ss')}
                            </MyAppText>
                        </View>
                        <MyAppText style={styles.cardContent}>{item.content}</MyAppText>
                    </View>
                    : null
                }

                {item.occurrenceReportType == 'historic' ?
                    <View style={{ gap: 10 }}>
                        <View>
                            <MyAppText style={styles.cardHeader}>{translate(content.title)}</MyAppText>
                            <MyAppText style={styles.cardSubtitle}>
                                {item.user ? (item.user.warName || item.user.name) + ' • ' : ''}
                                {moment(item.createdAt).format('DD/MM/YYYY HH:mm:ss')}
                            </MyAppText>
                        </View>
                        <MyAppText style={styles.cardContent}>
                            {
                                Object.keys(content).filter((key) => !['title', 'userId', 'actingBodyId', 'actingBodyCommandId', 'unitId'].includes(key)).map((key) => {
                                    if (['occurrence_updated_involved_object', 'occurrence_updated_involved_person', 'occurrence_updated_involved_vehicle'].includes(content.title)) {
                                        try {
                                            const [oldValue, newValue] = JSON.parse(content[key]);
                                            if (['gender', 'stolen', 'currency', 'arrested'].includes(key) && content[key]) {
                                                return `${translateWithoutErrors(key)}: ${translateWithoutErrors(String(oldValue))} => ${translateWithoutErrors(String(newValue))}`;
                                            }
                                            return `${translateWithoutErrors(key)}: ${oldValue} => ${newValue}`;
                                        } catch (err) {
                                            return `${translateWithoutErrors(key)}: ${content[key]}`;
                                        }
                                    }
                                    return `${translateWithoutErrors(key)}: ${translateWithoutErrors(String(content[key]))}`;
                                }
                                ).join('\n')
                            }
                        </MyAppText>
                    </View>
                    : null
                }
            </View>
        );
    }

    async function getOccurrenceHistoric(occurrenceId: number) {
        try {
            const resp = await appAgentService.getOccurrenceHistoricFiltered(Number(occurrenceId), filters);
            setHistoric(resp);
        } catch (err) {
            console.error(err);
            Toast.show({
                type: 'sentinelxError',
                text1: translate('unexpectedError'),
            });
        }
    }

    useEffect(() => {
        getOccurrenceHistoric(occurrenceId);
    }, [filters]);

    const handleCleanFilter = () => {
        setAttachment(false);
        setReport(false);
        setActions(false);
    };

    const handleFilter = () => {
        setFilters({
            ...filters,
            attachment: attachment ? attachment : undefined,
            report: report ? report : undefined,
            actions: actions ? actions : undefined,
        });
    };

    return <>
        <View style={styles.container}>
            <View style={{ flexDirection: 'row', justifyContent: 'flex-end', alignItems: 'center', columnGap: 24 }}>
                <TouchableOpacity
                    onPress={() => setIsModalVisible(true)}
                    style={{ paddingHorizontal: 16, paddingVertical: 13, backgroundColor: theme.colors.primaryButton.background, borderRadius: 4 }}
                >
                    <FontAwesomeIcon fontSize={16} fill='' icon={faFilter as IconDefinition} color={theme.colors.primaryButton.text} />
                </TouchableOpacity>
            </View>
            <View>
                <FlatList
                    extraData={true}
                    data={historic}
                    keyExtractor={(item) => item?.id?.toString() || '0'}
                    renderItem={renderHistoric}
                    contentContainerStyle={{ gap: 16 }}
                    style={{ flex: 1 }}
                />
            </View>
        </View>
        <Modal
            visible={isModalVisible}
            animationType='slide'
            onRequestClose={() => {
                setIsModalVisible(false);
            }}
            transparent={true}
        >
            <View style={[styles.modalContainer, { width: windowInfo.width, height: windowInfo.height }]}>
                {/* header */}
                <View style={styles.header}>
                    <View>
                        <MyAppText style={{ fontSize: 18 }}>{translate('allFilters')}</MyAppText>
                    </View>

                    <FontAwesomeIcon icon={faX} color={theme.colors.iconColor} fontSize={12} cursor='pointer' onClick={() => setIsModalVisible(false)} />
                </View>

                <View style={[styles.filterContainer]}>
                    <View style={styles.inputsHeight}>
                        <ScrollView contentContainerStyle={styles.scrollContent}>
                            <View style={styles.filters}>
                                <View style={{ flexDirection: 'column', gap: 10 }}>
                                    <MyAppText style={{ fontSize: 16 }}>{translate('registerTypes')}</MyAppText>
                                    <MyCheckbox
                                        label={translate('actions')}
                                        checked={actions}
                                        setChecked={(value) => {
                                            setActions(value);
                                        }}
                                    />
                                    <MyCheckbox
                                        label={translate('attachments')}
                                        checked={attachment}
                                        setChecked={(value) => {
                                            setAttachment(value);
                                        }}
                                    />
                                    <MyCheckbox
                                        label={translate('comments')}
                                        checked={report}
                                        setChecked={(value) => {
                                            setReport(value);
                                        }}
                                    />
                                </View>
                            </View>
                            <View style={styles.buttonsContainer}>
                                <CustomButton
                                    text={translate('filterResults')}
                                    onPress={() => {
                                        handleFilter();
                                        setIsModalVisible(false);
                                    }}
                                />
                                <CustomButton text={translate('cleanFilters')} onPress={handleCleanFilter} type='secondary' />
                            </View>
                        </ScrollView>
                    </View>
                </View>
            </View>
        </Modal>
        {url && <ImageModal
            imageUrl={url}
            isModalVisible={isImageModalVisible}
            setIsModalVisible={setIsImageModalVisible}
        />}
        {url && <VideoModal
            videoUrl={url}
            isModalVisible={isVideoModalVisible}
            setIsModalVisible={setIsVideoModalVisible}
        />}
    </>;
}

const styleSheet = createStyleSheet((theme) => ({
    modalContainer: {
        flex: 1,
        backgroundColor: theme.colors.container.background,
    },
    header: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: 15
    },
    filterContainer: {
        padding: 15,
        flex: 1
    },
    buttonsContainer: {
        flexDirection: 'column',
        gap: 10,
    },
    filters: {
        zIndex: 2,
        gap: 10
    },
    inputsHeight: {
        flex: 1,
    },
    scrollContent: {
        flex: 1,
        gap: 20,
        justifyContent: 'space-between'
    },
    container: {
        flex: 1,
        padding: 16,
        gap: 20
    },
    historicCard: {
        backgroundColor: theme.colors.card.background,
        borderWidth: 1,
        borderColor: theme.colors.card.border,
        borderRadius: 4,
        paddingVertical: 8,
        paddingHorizontal: 16,
        flex: 1
    },
    cardHeader: {
        fontWeight: 'bold',
        fontSize: 16,
        color: theme.colors.labelColor,
    },
    cardSubtitle: {
        fontSize: 14,
        color: theme.colors.disabledText,
    },
    cardContent: {
        fontSize: 16,
        color: theme.colors.labelColor,
    },
    imagePreview: {
        width: 150,
        height: 150,
        resizeMode: 'cover',
    },
}));
